export function removeTrailingSlash(url) {
    return url.replace(/\/+$/, ''); // Remove trailing slashes
}
// Only these schemes are safe to hand to `window.location.assign`; anything else
// (javascript:, data:, mailto:, ftp:, ...) must be rejected.
const ALLOWED_REDIRECT_PROTOCOLS = ['http:', 'https:'];
// A non-web scheme prefix: letters/digits/+/-/. followed by ':' that is NOT a
// port (a port would start with a digit, e.g. "example.com:8080"). Anchored so a
// leading protocol-relative "//" never matches.
const NON_WEB_SCHEME = /^[a-z][a-z0-9+.-]*:(?!\d)/i;
// Validates a candidate URL and returns its normalized `href` only if it parses
// to http(s).
const asAllowedUrl = (candidate) => {
    try {
        const url = new URL(candidate);
        return ALLOWED_REDIRECT_PROTOCOLS.includes(url.protocol) ? url.href : '';
    }
    catch (_a) {
        return '';
    }
};
/**
 * Normalizes a property-configured redirect URL (BENG-4196) into an absolute,
 * safe URL for navigation.
 *
 * The post-booking redirect setting stores whatever the hotelier typed, often
 * without a scheme (e.g. "www.example.com" or "example.com:8080/thanks"). Passed
 * straight to `window.location.assign`, a scheme-less value resolves RELATIVE to
 * the confirmation page and never leaves the Booking Engine origin, so the
 * redirect silently no-ops. This prepends `https://` when there is no scheme,
 * validates with the `URL` constructor, and allows only http(s) so the value
 * cannot become a `javascript:`/`data:` navigation vector.
 *
 * Returns '' for empty/blank/invalid input so callers keep the redirect notice
 * suppressed (an empty `redirectUrl` already gates the feature off).
 */
export function normalizeRedirectUrl(raw) {
    const trimmed = (raw !== null && raw !== void 0 ? raw : '').trim();
    if (!trimmed) {
        return '';
    }
    // Explicit http(s) scheme: validate and keep as-is.
    if (/^https?:\/\//i.test(trimmed)) {
        return asAllowedUrl(trimmed);
    }
    // Any OTHER explicit scheme (javascript:, data:, mailto:, ftp:, ...) is
    // rejected. Guard against a scheme-less "host:port" being mistaken for a
    // scheme: `new URL('example.com:8080/x')` would parse it as protocol
    // "example.com:" and drop a valid destination, so only treat a ':' that is
    // not followed by a digit as a scheme.
    if (!trimmed.startsWith('//') && NON_WEB_SCHEME.test(trimmed)) {
        return '';
    }
    // Scheme-less ("www.example.com", "example.com:8080/thanks", protocol-relative
    // "//host"): coerce to https, then validate.
    return asAllowedUrl(trimmed.startsWith('//') ? `https:${trimmed}` : `https://${trimmed}`);
}
